import { useState } from 'react';
import { Link } from '@inertiajs/react';
import { route } from '../../utils/route';
import type { ProductPack } from '../../types/packs';
import { PackItemsList } from './PackItemsList';
import { AddPackButton } from './AddPackButton';

interface PackCardProps {
  pack: ProductPack;
  isInCart: boolean;
  isLoading: boolean;
  onAdd: () => void;
  onRemove: () => void;
  detailRoute?: string;
}

function formatPrice(price: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

export function PackCard({ pack, isInCart, isLoading, onAdd, onRemove, detailRoute = 'packs.show' }: PackCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const detailUrl = route(detailRoute, { id: pack.slug || pack.id });
  const hasChoices = pack.has_choice_groups;

  const imageContent = pack.image_url ? (
    <img src={pack.image_url} alt={pack.title} className="h-full w-full object-cover" />
  ) : (
    <div className="flex h-full w-full items-center justify-center text-5xl text-gray-300">
      <i className="fa fa-box" />
    </div>
  );

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md">
      {/* Image */}
      <div className="h-[200px] w-full bg-gray-100">
        <Link href={detailUrl} className="block h-full w-full">{imageContent}</Link>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col p-4">
        {/* Header */}
        <div className="mb-2 flex items-start justify-between gap-3">
          <h3 className="m-0 flex-1 text-lg font-semibold leading-snug text-gray-800">
            <Link href={detailUrl} className="text-gray-800 no-underline hover:text-green-600">{pack.title}</Link>
          </h3>
          <span className="whitespace-nowrap text-xl font-bold text-green-600">
            {hasChoices && <span className="text-sm font-normal text-gray-500">Desde </span>}
            {formatPrice(pack.calculated_price)}
          </span>
        </div>

        {/* Description */}
        {pack.description && (
          <p className="mb-3 text-sm leading-relaxed text-gray-500">{pack.description}</p>
        )}

        {/* Items list */}
        <PackItemsList
          items={pack.items}
          choiceGroups={pack.choice_groups}
          isExpanded={isExpanded}
          onToggle={() => setIsExpanded(!isExpanded)}
        />

        {/* Actions */}
        <div className="mt-auto flex flex-col gap-2 pt-4">
          {hasChoices ? (
            <Link
              href={detailUrl}
              className="block rounded-md bg-green-600 px-4 py-2.5 text-center text-sm font-medium text-white no-underline transition-colors hover:bg-green-700"
            >
              Elegir opciones
            </Link>
          ) : (
            <>
              <AddPackButton isInCart={isInCart} isLoading={isLoading} onAdd={onAdd} onRemove={onRemove} />
              <Link
                href={detailUrl}
                className="block rounded-md border border-green-600 px-4 py-2.5 text-center text-sm font-medium text-green-600 no-underline transition-colors hover:bg-green-600 hover:text-white"
              >
                Ver Pack
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
